import React from 'react';
import type { components } from '../../../../types/learnerApi';

type Config = components['schemas']['SanitizedSingleChoiceConfig'];

export interface SingleChoiceAnswer {
  selected_id: string;
}

export interface SingleChoiceViewProps {
  item: { id: string; points?: number; config: Config };
  mode: 'learner' | 'preview';
  value?: SingleChoiceAnswer;
  onChange?: (value: SingleChoiceAnswer) => void;
  disabled?: boolean;
}

const SingleChoiceView: React.FC<SingleChoiceViewProps> = ({
  item,
  mode,
  value,
  onChange,
  disabled,
}) => {
  const selected = value?.selected_id ?? '';

  const select = (id: string) => {
    if (disabled || mode === 'preview') return;
    onChange?.({ selected_id: id });
  };

  return (
    <div className="space-y-4">
      {/* In learner mode the parent QuestionCard renders the prompt; in
          preview mode there's no parent prompt, so render it here. */}
      {mode === 'preview' && (
        <p className="font-medium text-text-primary">{item.config.question}</p>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {item.config.options.map((opt) => {
          const checked = selected === opt.id;
          return (
            <label
              key={opt.id}
              className={`group flex items-center gap-3 pl-4 sm:pl-5 pr-3 py-2.5 rounded-full border cursor-pointer transition-colors select-none ${
                checked
                  ? 'border-primary-500 bg-primary-50/80 dark:bg-primary-900/25 text-primary-700 dark:text-primary-200'
                  : 'border-border-secondary bg-surface-primary hover:border-primary-300 text-text-primary'
              } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <input
                type="radio"
                checked={checked}
                onChange={() => select(opt.id)}
                disabled={disabled}
                name={`single-choice-${item.id}`}
                className="sr-only"
              />
              <span className="flex-1 text-sm font-medium truncate">{opt.text}</span>
              <span
                className={`relative shrink-0 w-5 h-5 rounded-full border-[1.5px] transition-colors ${
                  checked ? 'border-primary-500' : 'border-text-secondary'
                }`}
              >
                {checked && (
                  <span className="absolute inset-[3px] rounded-full bg-primary-500" />
                )}
              </span>
            </label>
          );
        })}
      </div>

      {item.points != null && (
        <p className="text-xs text-text-tertiary">
          {item.points} pt{item.points !== 1 ? 's' : ''}
        </p>
      )}
    </div>
  );
};

export default SingleChoiceView;
